/**
 * Responsive image sources.
 *
 * Two mechanisms, one lookup:
 *
 * 1. Bundled files in `src/assets` — `vite-imagetools` generates AVIF and
 *    WebP variants at several widths at build time. We glob them eagerly and
 *    key the variants by the *single-format* URL the data files already
 *    import, so no data file has to change.
 * 2. Shopify CDN photos — the CDN resizes on demand via a `width` query
 *    parameter, so a srcset is just string building.
 *
 * Variants are only ever generated downward from the original, so no screen
 * ever receives an upscaled image.
 */

import hiddenGreenVelvet from "@/assets/hidden-green-velvet.png.asset.json";
import hiddenGreenVelvet400 from "@/assets/hidden-green-velvet-400.webp.asset.json";
import hiddenGreenVelvet800 from "@/assets/hidden-green-velvet-800.webp.asset.json";
import hiddenGreenVelvet1100 from "@/assets/hidden-green-velvet-1100.webp.asset.json";
import hiddenInHand from "@/assets/hidden-in-hand.png.asset.json";
import hiddenInHand400 from "@/assets/hidden-in-hand-400.webp.asset.json";
import hiddenInHand800 from "@/assets/hidden-in-hand-800.webp.asset.json";
import hiddenInHand1100 from "@/assets/hidden-in-hand-1100.webp.asset.json";

export const DEFAULT_WIDTHS = [400, 800, 1280] as const;

/** Finer ladder for CDN photos, so the browser can land close to the real size. */
export const CDN_WIDTHS = [320, 480, 640, 720, 960, 1280] as const;

type SrcSetMap = Record<string, string>;

/** The URL each asset resolves to today (`?format=webp&quality=72` imports). */
const baseUrls = import.meta.glob("/src/assets/*.{jpg,jpeg,png}", {
  query: "?format=webp&quality=72",
  import: "default",
  eager: true,
}) as Record<string, string>;

const avifSets = import.meta.glob("/src/assets/*.{jpg,jpeg,png}", {
  query: "?w=400;800;1280&format=avif&quality=55&as=srcset",
  import: "default",
  eager: true,
}) as Record<string, string>;

const webpSets = import.meta.glob("/src/assets/*.{jpg,jpeg,png}", {
  query: "?w=400;800;1280&format=webp&quality=78&as=srcset",
  import: "default",
  eager: true,
}) as Record<string, string>;

const avifByUrl: SrcSetMap = {};
const webpByUrl: SrcSetMap = {};

for (const [path, url] of Object.entries(baseUrls)) {
  if (typeof url !== "string") continue;
  const avif = avifSets[path];
  const webp = webpSets[path];
  if (typeof avif === "string") avifByUrl[url] = avif;
  if (typeof webp === "string") webpByUrl[url] = webp;
}

// CDN asset-pointer photos have no on-demand resizer, so their width
// variants were generated once and uploaded alongside the original.
webpByUrl[hiddenGreenVelvet.url] = [
  `${hiddenGreenVelvet400.url} 400w`,
  `${hiddenGreenVelvet800.url} 800w`,
  `${hiddenGreenVelvet1100.url} 1100w`,
].join(", ");
webpByUrl[hiddenInHand.url] = [
  `${hiddenInHand400.url} 400w`,
  `${hiddenInHand800.url} 800w`,
  `${hiddenInHand1100.url} 1100w`,
].join(", ");

export function isShopifyImage(url: string): boolean {
  try {
    const { hostname } = new URL(url);
    return hostname === "cdn.shopify.com" || hostname.endsWith(".cdn.shopify.com");
  } catch {
    return url.includes("cdn/shop/");
  }
}

type ShopifyImageOpts = {
  /** width / height of the rendered box, so the CDN can also cap height. */
  aspect?: number;
  /** Ask the CDN to transcode (PNG product art is photographic). */
  format?: "webp" | "jpg" | "pjpg";
};

/** Append Shopify's on-demand resize parameters to a CDN image URL. */
export function shopifyImageUrl(url: string, width: number, opts: ShopifyImageOpts = {}): string {
  try {
    const u = new URL(url, "https://cdn.shopify.com");
    u.searchParams.set("width", String(width));
    if (opts.aspect && Number.isFinite(opts.aspect) && opts.aspect > 0) {
      u.searchParams.set("height", String(Math.round(width / opts.aspect)));
      u.searchParams.set("crop", "center");
    }
    if (opts.format) u.searchParams.set("format", opts.format);
    return u.toString();
  } catch {
    return url;
  }
}

export function shopifyImageSrcSet(
  url: string,
  widths: readonly number[] = CDN_WIDTHS,
  opts: ShopifyImageOpts = {},
): string {
  return widths.map((w) => `${shopifyImageUrl(url, w, opts)} ${w}w`).join(", ");
}

export type ResolvedSources = {
  /** Fallback `src` for the `<img>`. */
  src: string;
  avifSrcSet?: string;
  webpSrcSet?: string;
  /** Generic srcset (same format as `src`) when no format variants exist. */
  srcSet?: string;
};

/**
 * Resolve the best available responsive sources for any image URL the app
 * renders: bundled asset, Shopify CDN photo, or an opaque remote URL.
 */
export function resolveImageSources(
  src: string,
  widths: readonly number[] = DEFAULT_WIDTHS,
  aspect?: number,
): ResolvedSources {
  if (!src) return { src };

  const avifSrcSet = avifByUrl[src];
  const webpSrcSet = webpByUrl[src];
  if (avifSrcSet || webpSrcSet) return { src, avifSrcSet, webpSrcSet };

  if (isShopifyImage(src)) {
    const ladder = widths === DEFAULT_WIDTHS ? CDN_WIDTHS : widths;
    const largest = ladder[ladder.length - 1]!;
    return {
      // Shopify's CDN negotiates WebP/AVIF from the browser's Accept header,
      // so one srcset is enough — a `format=` param would be a no-op.
      src: shopifyImageUrl(src, largest, { aspect }),
      srcSet: shopifyImageSrcSet(src, ladder, { aspect }),
    };
  }

  return { src };
}

/** Common `sizes` presets, so placements declare their real rendered width. */
export const SIZES = {
  /** Full-bleed hero. */
  hero: "100vw",
  /** 1-up on mobile, 2-up tablet, 3-up desktop grid cards (container-capped). */
  card: "(min-width: 1280px) 400px, (min-width: 1024px) 32vw, (min-width: 640px) 45vw, 88vw",
  /** Large product hero inside a two-column product layout. */
  product: "(min-width: 1280px) 600px, (min-width: 1024px) 48vw, 94vw",
  /** Small gallery thumbnails (w-28 = 112px). */
  thumb: "112px",
  /** Cart line item / drawer thumbnails. */
  cartThumb: "96px",
  /** Note / character portrait cards. */
  portrait: "(min-width: 1280px) 300px, (min-width: 1024px) 24vw, (min-width: 640px) 40vw, 70vw",
} as const;
